/**
 * Demo adapter runtime: simulated latency plus a QA switch for UI states.
 * Append `?simulate=error`, `?simulate=empty` or `?simulate=slow` to any URL to exercise
 * error, empty and loading states (UX-003, M1-09).
 */
export type Simulation = 'none' | 'error' | 'empty' | 'slow';

export function currentSimulation(): Simulation {
  if (typeof window === 'undefined') return 'none';
  const value = new URLSearchParams(window.location.search).get('simulate');
  return value === 'error' || value === 'empty' || value === 'slow' ? value : 'none';
}

const BASE_LATENCY_MS = import.meta.env.MODE === 'test' ? 0 : 350;

export class DemoServiceError extends Error {
  constructor(message = 'The demo service could not complete this request.') {
    super(message);
    this.name = 'DemoServiceError';
  }
}

/** Resolve demo data after simulated latency, honouring the QA simulation switch. */
export async function demoResponse<T>(value: T, empty: T): Promise<T> {
  const simulation = currentSimulation();
  const delay = simulation === 'slow' ? 4000 : BASE_LATENCY_MS;
  if (delay > 0) await new Promise((resolve) => setTimeout(resolve, delay));
  if (simulation === 'error') throw new DemoServiceError();
  return simulation === 'empty' ? empty : value;
}
